import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  PaperAirplaneIcon,
  SparklesIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { aiApi } from '../../services/aiApi';
import { api } from '../../services/api';
import { useAmazonProfile } from '../../contexts/AmazonProfileContext';
import Button from '../ui/Button';

const MCP_PREF_KEY = 'sellerclub-assistant-amazon-mcp';

/** Compact FAB + panel sit above table pagination (bottom ~3rem). */
const LAUNCHER_CLASS =
  'fixed bottom-3 right-3 z-30 flex h-9 w-9 items-center justify-center rounded-full bg-orange-600 text-white shadow-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 dark:focus:ring-offset-stone-950 sm:bottom-4 sm:right-4 sm:h-10 sm:w-10';
const PANEL_ANCHOR_CLASS = 'bottom-[3.25rem] right-3 sm:bottom-[3.75rem] sm:right-4';

function formatAssistantText(text) {
  const lines = String(text || '').split('\n');
  return lines.map((line, lineIdx) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    const children = parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={i} className="font-semibold text-slate-900 dark:text-slate-100">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return <span key={i}>{part}</span>;
    });
    return (
      <p key={lineIdx} className={line.trim() ? 'mb-1.5 last:mb-0' : 'mb-2'}>
        {children}
      </p>
    );
  });
}

function buildWelcome(amazonMcpOn, mcpConnected, pagePath) {
  const pageHint = pagePath && pagePath !== '/dashboard'
    ? ` You're on **${pagePath}** — ask "what can I do here?" for page-specific help.`
    : '';
  if (!amazonMcpOn) {
    return {
      role: 'assistant',
      content:
        `Hi — I'm your **SellerClub ads assistant**. I use synced reports, campaign data, and know which page you're on in the app.${pageHint} Turn **Amazon MCP** on below for live Amazon tools when approved.`,
      suggestions: [
        'What can I do on this page?',
        'What is spend and sales today?',
        'Show optimization recommendations',
        'Any anomalies?',
      ],
    };
  }
  if (mcpConnected) {
    return {
      role: 'assistant',
      content:
        'Hi — **Amazon MCP is on.** I\'ll try Amazon\'s MCP tools first, then fall back to SellerClub synced reports if a tool fails. Turn MCP off anytime to use only your database.',
      suggestions: [
        'What is spend and sales today?',
        'Show optimization recommendations',
        'List MCP tools',
      ],
    };
  }
  return {
    role: 'assistant',
    content:
      'Hi — **Amazon MCP is on** but not connected right now. Answers will use **SellerClub cache** until MCP auth works.',
    suggestions: [
      'What is spend and sales today?',
      'Show optimization recommendations',
    ],
  };
}

function readMcpPref() {
  try {
    const v = localStorage.getItem(MCP_PREF_KEY);
    if (v === 'false') return false;
    if (v === 'true') return true;
  } catch {
    // ignore
  }
  return true;
}

export default function DashboardAssistantChat() {
  const { selectedProfile } = useAmazonProfile();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [amazonMcpOn, setAmazonMcpOn] = useState(readMcpPref);
  const [messages, setMessages] = useState([buildWelcome(readMcpPref(), false, location.pathname)]);
  const [mcpStatus, setMcpStatus] = useState(null);
  const [applyingActionId, setApplyingActionId] = useState(null);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const scrollRef = useRef(null);

  const profileId = selectedProfile?.profileId;
  const storeLabel = selectedProfile?.accountInfo?.name
    || selectedProfile?.accountName
    || selectedProfile?.profileId
    || 'No store selected';

  const mcpConnected = Boolean(mcpStatus?.connected);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, loading]);

  useEffect(() => {
    if (!open || !profileId) {
      setMcpStatus(null);
      return;
    }
    api.get('/agentic/mcp/status', { params: { profileId } })
      .then((res) => setMcpStatus(res.data?.data || null))
      .catch(() => setMcpStatus(null));
  }, [open, profileId]);

  const setMcpPref = useCallback((on) => {
    setAmazonMcpOn(on);
    try {
      localStorage.setItem(MCP_PREF_KEY, on ? 'true' : 'false');
    } catch {
      // ignore
    }
  }, []);

  const endSession = useCallback(() => {
    setMessages([buildWelcome(amazonMcpOn, mcpConnected, location.pathname)]);
    setInput('');
  }, [amazonMcpOn, mcpConnected, location.pathname]);

  useEffect(() => {
    if (!open) return;
    setMessages((prev) => {
      if (prev.length !== 1 || prev[0].role !== 'assistant') return prev;
      return [buildWelcome(amazonMcpOn, mcpConnected, location.pathname)];
    });
  }, [location.pathname, open, amazonMcpOn, mcpConnected]);

  const sendMessage = useCallback(async (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || loading) return;

    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setInput('');
    setPendingConfirm(null);
    setLoading(true);

    const preferAmazonMcp = amazonMcpOn && mcpStatus?.enabled !== false;

    try {
      const response = await aiApi.dashboardChat({
        profileId,
        message: trimmed,
        preferAmazonMcp,
        pageContext: {
          path: location.pathname,
          search: location.search,
          title: document.title,
        },
      });
      const data = response.data;
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.reply || 'No response.',
          suggestions: data.suggestions,
          actions: data.actions,
          source: data.meta?.source || (data.meta?.orchestrator ? 'tool_orchestrator' : undefined),
          meta: data.meta,
        },
      ]);
    } catch (error) {
      const errMsg = error.response?.data?.error || error.message || 'Request failed';
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Could not reach the assistant: ${errMsg}` },
      ]);
    } finally {
      setLoading(false);
    }
  }, [loading, profileId, amazonMcpOn, mcpStatus?.enabled, location.pathname, location.search]);

  const executeWriteAction = useCallback(async (action) => {
    if (action.type === 'intelligence_apply' && action.candidate?.action?.type) {
      const res = await aiApi.applyIntelligence({
        profileId,
        candidate: action.candidate,
      });
      if (res.data?.applied === false) {
        throw new Error(res.data?.message || 'Not applied');
      }
      return res.data?.message || 'Action applied';
    }
    if (action.type === 'agentic_apply' && action.recommendation?.action?.type) {
      const res = await aiApi.applyRecommendation({
        profileId,
        recommendation: action.recommendation,
      });
      if (res.data?.applied === false) {
        return { toastInfo: res.data?.message || 'Needs manual follow-up' };
      }
      return res.data?.message || 'Applied';
    }
    return null;
  }, [profileId]);

  const removeMessageActions = useCallback((messageIndex, removeIds) => {
    const idSet = new Set(removeIds.filter(Boolean));
    setMessages((prev) => prev.map((msg, i) => {
      if (i !== messageIndex || !msg.actions?.length) return msg;
      return {
        ...msg,
        actions: msg.actions.filter((a) => !idSet.has(a.id) && !removeIds.includes(a)),
      };
    }));
  }, []);

  const runConfirmedAction = useCallback(async (messageIndex, action) => {
    if (!profileId || !action?.type) return;
    const actionKey = action.id || action.label || String(messageIndex);
    setApplyingActionId(actionKey);
    setPendingConfirm(null);
    try {
      const batch = action.batch || (action.type.startsWith('bulk_') ? action.batch : null);
      const steps = batch?.length ? batch : [action];
      let ok = 0;
      let lastMsg = '';
      for (const step of steps) {
        // eslint-disable-next-line no-await-in-loop
        const outcome = await executeWriteAction(step);
        if (outcome && typeof outcome === 'object' && outcome.toastInfo) {
          toast(outcome.toastInfo, { icon: 'ℹ️' });
        } else if (outcome) {
          ok += 1;
          lastMsg = outcome;
        }
      }
      if (ok > 0) {
        toast.success(ok > 1 ? `${ok} actions applied` : lastMsg);
      }
      const removeIds = steps.map((s) => s.id).concat(action.id);
      removeMessageActions(messageIndex, removeIds);
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Apply failed');
    } finally {
      setApplyingActionId(null);
    }
  }, [profileId, executeWriteAction, removeMessageActions]);

  const requestChatAction = useCallback((messageIndex, action) => {
    if (!profileId || !action?.type) return;
    const needsConfirm = action.type === 'intelligence_apply'
      || action.type === 'agentic_apply'
      || action.type === 'bulk_intelligence_apply'
      || action.type === 'bulk_agentic_apply';
    if (!needsConfirm) return;
    setPendingConfirm({
      messageIndex,
      action,
      confirmMessage: action.confirmMessage || 'Apply this change on your Amazon ads account?',
    });
  }, [profileId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={LAUNCHER_CLASS}
          aria-label="Open ads assistant"
          title="Ads assistant"
        >
          <ChatBubbleLeftRightIcon className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
        </button>
      )}

      {open && (
        <div
          className={`fixed ${PANEL_ANCHOR_CLASS} z-40 flex w-[min(calc(100vw-1.25rem),320px)] flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900`}
          style={{ height: 'min(420px, calc(100vh - 5.5rem))' }}
        >
          <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2 dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-center gap-1.5 min-w-0">
              <SparklesIcon className="h-4 w-4 text-orange-600 shrink-0" />
              <div className="min-w-0">
                <p className="text-xs font-semibold text-slate-900 dark:text-slate-100 truncate">Ads assistant</p>
                <p className="text-[10px] text-slate-500 truncate">
                  {storeLabel}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg p-1 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"
              aria-label="Close"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-white px-2.5 py-1.5 dark:border-slate-700 dark:bg-slate-900">
            <label className="flex cursor-pointer items-center gap-1.5 text-[10px] text-slate-700 dark:text-slate-300">
              <span className="font-medium">MCP</span>
              <button
                type="button"
                role="switch"
                aria-checked={amazonMcpOn}
                onClick={() => setMcpPref(!amazonMcpOn)}
                className={`relative h-4 w-7 shrink-0 rounded-full transition-colors ${
                  amazonMcpOn ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform ${
                    amazonMcpOn ? 'translate-x-3' : ''
                  }`}
                />
              </button>
            </label>
            <button
              type="button"
              onClick={endSession}
              className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
            >
              <ArrowPathIcon className="h-3 w-3" />
              New chat
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-2 space-y-2">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[92%] rounded-2xl px-3 py-2 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-orange-600 text-white'
                      : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200'
                  }`}
                >
                  {msg.role === 'user' ? msg.content : formatAssistantText(msg.content)}
                  {(msg.source || msg.meta?.orchestrator) && msg.role === 'assistant' && (
                    <p className="mt-2 text-[10px] uppercase tracking-wide text-slate-500">
                      via {String(msg.source || 'tool_orchestrator').replace(/_/g, ' ')}
                      {Array.isArray(msg.meta?.toolsUsed) && msg.meta.toolsUsed.length > 0 && (
                        <span className="normal-case"> · {msg.meta.toolsUsed.join(', ')}</span>
                      )}
                      {msg.meta?.planner && (
                        <span className="normal-case"> · planner {msg.meta.planner}</span>
                      )}
                    </p>
                  )}
                  {msg.actions?.length > 0 && (
                    <div className="mt-2 flex flex-col gap-1.5">
                      {msg.actions.map((action, i) => {
                        if (action.href) {
                          return (
                            <Link
                              key={i}
                              to={action.href}
                              className="text-xs font-medium text-orange-600 hover:underline dark:text-orange-400"
                              onClick={() => setOpen(false)}
                            >
                              {action.label}
                            </Link>
                          );
                        }
                        if (
                          action.type === 'intelligence_apply'
                          || action.type === 'agentic_apply'
                          || action.type === 'bulk_intelligence_apply'
                          || action.type === 'bulk_agentic_apply'
                        ) {
                          const busy = applyingActionId === (action.id || action.label);
                          return (
                            <Button
                              key={action.id || i}
                              type="button"
                              size="sm"
                              variant={action.type.startsWith('bulk_') ? 'primary' : 'secondary'}
                              disabled={busy || !profileId}
                              className="!h-7 !px-2 !text-[11px] !justify-start"
                              onClick={() => requestChatAction(idx, action)}
                            >
                              {busy ? 'Applying…' : action.label}
                            </Button>
                          );
                        }
                        return null;
                      })}
                      {pendingConfirm?.messageIndex === idx && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-2 dark:border-amber-500/40 dark:bg-amber-950/40">
                          <p className="text-[11px] text-amber-900 dark:text-amber-100">
                            {pendingConfirm.confirmMessage}
                          </p>
                          <div className="mt-2 flex gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="primary"
                              className="!h-7 !px-2 !text-[11px]"
                              disabled={Boolean(applyingActionId)}
                              onClick={() => runConfirmedAction(idx, pendingConfirm.action)}
                            >
                              Confirm
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              className="!h-7 !px-2 !text-[11px]"
                              disabled={Boolean(applyingActionId)}
                              onClick={() => setPendingConfirm(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {msg.suggestions?.length > 0 && msg.role === 'assistant' && idx === messages.length - 1 && !loading && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {msg.suggestions.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => sendMessage(s)}
                          className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300"
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="text-xs text-slate-500 animate-pulse px-1">Thinking…</div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="border-t border-slate-200 p-2 dark:border-slate-700">
            {!profileId && (
              <p className="text-[10px] text-amber-700 mb-1.5">Select a store for account-specific answers.</p>
            )}
            <div className="flex gap-1.5">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about spend, ACOS…"
                disabled={loading}
                className="flex-1 rounded-lg border border-slate-300 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
              />
              <Button type="submit" disabled={loading || !input.trim()} className="!px-2 !py-1.5" aria-label="Send">
                <PaperAirplaneIcon className="h-3.5 w-3.5" />
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
